import {
    BS_IMAGE_CAPTION_TYPE,
    BS_IMAGE_FIGURE_TYPE,
} from './constants';
import {
    createFigureCaptionModel,
    createFigureDefaults,
    initCaptionModel,
    initFigureCaptionModel,
    isBsImageCaptionElement,
    isBsImageFigureElement,
    wrapLegacyImageIfNeeded,
} from './figureModel';
import { getCaptionText, resolveBsImageFigure } from './utils';

function syncAllCaptionVisibility(editor, editingFigure = null) {
    const wrapper = editor.getWrapper();
    if (!wrapper) return;

    wrapper.onAll((comp) => {
        if (comp.get('type') !== BS_IMAGE_FIGURE_TYPE) return;
        const isEditing = Boolean(editingFigure && comp.cid === editingFigure.cid);
        comp.syncCaptionVisibility(isEditing);
    });
}

function refreshCaptionState(editor) {
    const editing = editor.getEditing?.();
    if (editing?.get('type') === BS_IMAGE_CAPTION_TYPE) {
        resolveBsImageFigure(editing)?.syncCaptionVisibility(true);
        return;
    }

    const figure = resolveBsImageFigure(editor.getSelected());
    syncAllCaptionVisibility(editor, figure);

    if (!figure) return;

    const caption = figure.getCaptionComponent();
    if (caption && getCaptionText(caption)) {
        caption.removeClass('sio-image-caption--hidden');
        caption.removeClass('sio-image-caption--editing');
        return;
    }

    figure.ensureCaptionForEditing();
}

function createCaptionEditorState(editor) {
    let captionRefreshTimer;

    return {
        scheduleCaptionRefresh() {
            clearTimeout(captionRefreshTimer);
            captionRefreshTimer = setTimeout(() => refreshCaptionState(editor), 20);
        },
        refreshCaptionState: () => refreshCaptionState(editor),
        syncAllCaptionVisibility: (editingFigure) =>
            syncAllCaptionVisibility(editor, editingFigure),
    };
}

function activateCaptionEditing(editor, textView, captionModel, event) {
    if (!captionModel) return;

    const figure = captionModel.parent();
    if (figure?.get('type') === BS_IMAGE_FIGURE_TYPE) {
        figure.ensureCaptionForEditing();
    }

    const run = () => {
        editor.select(captionModel);
        const view = captionModel.view;
        if (!view) return;

        if (typeof view.onActive === 'function') {
            view.onActive(event);
        } else if (typeof textView.prototype?.onActive === 'function') {
            textView.prototype.onActive.call(view, event);
        } else {
            captionModel.trigger('active', event);
        }
    };

    if (captionModel.view) {
        run();
    } else {
        captionModel.once('render', run);
    }
}

export function registerCaptionComponent(domComponents, editor, textModel, textView) {
    const captionEditor = createCaptionEditorState(editor);

    domComponents.addType(BS_IMAGE_CAPTION_TYPE, {
        extend: 'text',
        model: {
            defaults: Object.assign({}, textModel.prototype.defaults, {
                'custom-name': 'Image Caption',
                tagName: 'figcaption',
                editable: true,
                draggable: false,
                droppable: false,
                removable: false,
                copyable: false,
                selectable: true,
                highlightable: true,
                classes: ['sio-image-caption'],
                content: '',
            }),
            init: initCaptionModel,
        },
        isComponent(el) {
            if (!isBsImageCaptionElement(el)) return;
            return { type: BS_IMAGE_CAPTION_TYPE };
        },
        view: textView.extend({
            events: Object.assign({}, textView.prototype.events || {}, {
                click: 'enableCaptionEditing',
                dblclick: 'enableCaptionEditing',
                mousedown: 'onCaptionMouseDown',
                focusin: 'onCaptionFocus',
                focusout: 'onCaptionBlur',
            }),
            onCaptionMouseDown() {
                this.model.parent()?.ensureCaptionForEditing?.();
            },
            enableCaptionEditing(e) {
                activateCaptionEditing(editor, textView, this.model, e);
            },
            onCaptionFocus() {
                const figure = this.model.parent();
                if (figure?.get('type') === BS_IMAGE_FIGURE_TYPE) {
                    figure.syncCaptionVisibility(true);
                }
            },
            onCaptionBlur() {
                if (this.model.parent()?.get('type') !== BS_IMAGE_FIGURE_TYPE) return;
                captionEditor.scheduleCaptionRefresh();
            },
        }),
    });

    return captionEditor;
}

export function registerCaptionEditorEvents(editor, captionEditor) {
    editor.on('component:selected', (component) => {
        wrapLegacyImageIfNeeded(editor, component);

        if (component.get('type') === BS_IMAGE_FIGURE_TYPE) {
            return;
        }

        if (component.get('type') !== BS_IMAGE_CAPTION_TYPE) {
            captionEditor.refreshCaptionState();
            return;
        }

        const figure = resolveBsImageFigure(component);
        if (figure) {
            captionEditor.syncAllCaptionVisibility(figure);
            figure.ensureCaptionForEditing();
        }

        const alreadyEditing = editor.getEditing?.()?.cid === component.cid;
        if (!alreadyEditing) {
            const textView = editor.DomComponents.getType('text').view;
            activateCaptionEditing(editor, textView, component, null);
        }
    });

    editor.on('component:deselected', () => {
        captionEditor.scheduleCaptionRefresh();
    });

    editor.on('rte:disable', (view) => {
        if (view?.model?.get('type') !== BS_IMAGE_CAPTION_TYPE) return;
        captionEditor.scheduleCaptionRefresh();
    });

    editor.on('load', () => {
        captionEditor.syncAllCaptionVisibility(null);
    });
}

export function registerFigureCaptionType(domComponents) {
    domComponents.addType(BS_IMAGE_FIGURE_TYPE, {
        model: {
            defaults: createFigureDefaults(),
            init: initFigureCaptionModel,
            ...createFigureCaptionModel(),
        },
        isComponent(el) {
            if (!isBsImageFigureElement(el)) return;
            return { type: BS_IMAGE_FIGURE_TYPE };
        },
    });
}
